// ** Modules
import React from 'react';

// ** UI
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import { SelectChevronIcon } from '@/components/common/icons';

// ** Types
import type { SearchOption, SearchSectionSelectProps } from './types';

const BORDER_WIDTH = '1.4px';
const MENU_MAX_HEIGHT = 324;
const ITEM_DIVIDER = '1px solid rgba(0, 0, 0, 0.18)';
/** Must sit above `MegaMenuOverlay` (z-index 1600) so portaled menus are visible and clickable. */
const OVERLAY_MENU_Z_INDEX = 1700;
const PERIOD_WEIGHTS: Record<string, number> = {
	'7_days': 10,
	'14_days': 20,
	'30_days': 30,
	'90_plus': 40
};

const smartSortOptions = (opts: SearchOption[]): SearchOption[] => {
	if (!Array.isArray(opts)) {
		return [];
	}

	return [...opts].sort((a, b) => {
		if (a.value === 'all') return -1;
		if (b.value === 'all') return 1;

		const weightA = PERIOD_WEIGHTS[a.value] ?? null;
		const weightB = PERIOD_WEIGHTS[b.value] ?? null;

		if (weightA !== null && weightB !== null) {
			return weightA - weightB;
		}
		if (weightA !== null) return -1;
		if (weightB !== null) return 1;

		const isYearA = /^\d{4}$/.test(a.label);
		const isYearB = /^\d{4}$/.test(b.label);

		if (isYearA && isYearB) {
			return b.label.localeCompare(a.label);
		}

		return a.label.localeCompare(b.label, 'de');
	});
};

const SearchSectionSelect = ({
	id,
	value,
	placeholder,
	options,
	onChange,
	disabled = false,
	backgroundColor,
	appearance = 'default',
	sx
}: SearchSectionSelectProps) => {
	const theme = useTheme();
	const sortedOptions = React.useMemo(() => smartSortOptions(options), [options]);
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const fieldRef = React.useRef<HTMLDivElement>(null);
	/** When backdrop is pointer-events:none, MUI still calls `onOpen` after mousedown; skip that open when we meant to toggle closed. */
	const suppressNextOpenRef = React.useRef(false);
	const [paperWidth, setPaperWidth] = React.useState<number | undefined>(undefined);
	const isOverlay = appearance === 'overlay';
	const white = theme.palette.common.white;
	const fieldBg = isOverlay
		? theme.palette.loginOverlayCta.main
		: (backgroundColor ?? theme.palette.heroLightBackground.main);
	const black = theme.palette.common.black;
	const textSecondary = theme.palette.text.secondary;
	const hoverText = theme.palette.selectHoverText.main;
	const borderColor = isOverlay ? alpha(white, 0.45) : black;
	const selectTextColor = isOverlay ? white : black;
	const iconColor = isOverlay ? alpha(white, 0.75) : textSecondary;
	const menuItemColor = isOverlay ? white : textSecondary;
	const menuHoverHighlight = isOverlay ? alpha(white, 0.14) : black;
	const menuHoverTextColor = isOverlay ? white : hoverText;
	const itemDividerColor = isOverlay ? '1px solid rgba(255, 255, 255, 0.22)' : ITEM_DIVIDER;
	const triggerMenuHairline = isOverlay ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.18)';

	React.useEffect(() => {
		const el = fieldRef.current;
		if (!el) return;
		const update = () => setPaperWidth(el.offsetWidth);
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	React.useEffect(() => {
		if (!isOverlay || !isMenuOpen) return;

		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (fieldRef.current?.contains(target)) return;
			const openPopovers = document.querySelectorAll('.MuiPopover-root');
			for (const popover of openPopovers) {
				if (popover.contains(target)) return;
			}
			setIsMenuOpen(false);
		};

		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [isOverlay, isMenuOpen]);

	React.useEffect(() => {
		if (!isOverlay || !isMenuOpen) return;

		const handleScroll = (event: Event) => {
			const target = event.target as Node;
			const openPopovers = document.querySelectorAll('.MuiPopover-root');
			for (const popover of openPopovers) {
				if (popover.contains(target)) return;
			}
			setIsMenuOpen(false);
		};

		document.addEventListener('scroll', handleScroll, true);
		return () => document.removeEventListener('scroll', handleScroll, true);
	}, [isOverlay, isMenuOpen]);

	const outlineSx = {
		borderColor,
		borderWidth: BORDER_WIDTH,
		borderBottomWidth: isMenuOpen ? 0 : BORDER_WIDTH
	};

	return (
		<TextField
			ref={fieldRef}
			id={id}
			select
			fullWidth
			disabled={disabled}
			value={value ?? ''}
			onChange={(event) => onChange?.(event.target.value || null)}
			slotProps={{
				select: {
					open: isMenuOpen,
					displayEmpty: true,
					onOpen: () => {
						if (suppressNextOpenRef.current) {
							suppressNextOpenRef.current = false;
							return;
						}
						setIsMenuOpen(true);
					},
					onClose: () => setIsMenuOpen(false),
					...(isOverlay && {
						inputProps: {
							onMouseDown: () => {
								if (!isMenuOpen) return;
								suppressNextOpenRef.current = true;
								setIsMenuOpen(false);
							}
						}
					}),
					IconComponent: SelectChevronIcon,
					SelectDisplayProps: { 'aria-label': placeholder },
					renderValue: (selected) => {
						const v = String(selected ?? '');
						return sortedOptions.find((o) => o.value === v)?.label ?? placeholder;
					},
					MenuProps: {
						marginThreshold: 0,
						...(isOverlay && {
							sx: { zIndex: OVERLAY_MENU_Z_INDEX, pointerEvents: 'none' },
							disableScrollLock: true
						}),
						slotProps: {
							...(isOverlay && {
								backdrop: { sx: { pointerEvents: 'none' } }
							}),
							list: {
								disablePadding: true,
								autoFocusItem: true,
								sx: {
									p: 0,
									backgroundColor: fieldBg,
									maxHeight: MENU_MAX_HEIGHT,
									overflowY: 'auto',
									overflowX: 'hidden',
									overscrollBehaviorY: 'contain',
									WebkitOverflowScrolling: 'touch'
								}
							},
							paper: {
								// Inline background so Chrome shows it during overscroll bounce
								style:
									paperWidth !== undefined
										? {
												width: paperWidth,
												minWidth: paperWidth,
												backgroundColor: fieldBg
											}
										: { backgroundColor: fieldBg },
								sx: {
									pointerEvents: 'auto',
									bgcolor: fieldBg,
									borderRadius: '0 0 8px 8px',
									border: `${BORDER_WIDTH} solid`,
									borderColor,
									borderTop: 0,
									boxShadow: 'none',
									overflow: 'hidden',
									overscrollBehaviorY: 'contain',
									transition: 'border-color 180ms ease, border-radius 180ms ease, box-shadow 180ms ease',
									'& .MuiList-root': {
										p: 0,
										bgcolor: fieldBg
									},
									'& .MuiMenuItem-root': {
										backgroundColor: fieldBg,
										color: menuItemColor,
										minHeight: 54,
										borderRadius: 0,
										pl: 4,
										pr: 8,
										position: 'relative',
										zIndex: 0,
										fontFamily: 'inherit',
										fontSize: 18,
										fontWeight: 400,
										lineHeight: '24px',
										letterSpacing: '-0.05px',
										whiteSpace: 'normal',
										wordBreak: 'break-word',
										'&::before': {
											content: '""',
											position: 'absolute',
											top: 0,
											bottom: 0,
											left: 4,
											right: 8,
											borderRadius: 2,
											backgroundColor: 'transparent',
											pointerEvents: 'none',
											zIndex: -1
										},
										'&:hover::before, &.Mui-selected:hover::before, &.Mui-focusVisible::before': {
											backgroundColor: menuHoverHighlight
										},
										'&:hover, &.Mui-selected:hover, &.Mui-focusVisible': {
											color: menuHoverTextColor
										},
										'&.Mui-selected': {
											backgroundColor: fieldBg,
											color: menuHoverTextColor,
											'&::before': {
												backgroundColor: menuHoverHighlight
											},
											'&:hover': {
												backgroundColor: fieldBg
											},
											'&.Mui-focusVisible': {
												backgroundColor: fieldBg
											}
										}
									},
									'& .MuiMenuItem-root:not(:last-of-type)': {
										borderBottom: itemDividerColor
									},
									[theme.breakpoints.down('md')]: {
										'& .MuiMenuItem-root::before': {
											left: '0 !important',
											right: '0 !important',
											borderRadius: '0 !important'
										}
									}
								}
							}
						}
					}
				}
			}}
			sx={[
				{
					'& .MuiOutlinedInput-root': {
						height: 48,
						borderRadius: isMenuOpen ? '8px 8px 0 0' : '8px',
						bgcolor: fieldBg,
						position: 'relative',
						transition: 'border-radius 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
						...(isMenuOpen && {
							'&::after': {
								content: '""',
								position: 'absolute',
								bottom: 0,
								left: 0,
								right: 0,
								height: '1px',
								backgroundColor: triggerMenuHairline,
								pointerEvents: 'none'
							}
						})
					},
					'& .MuiOutlinedInput-notchedOutline': outlineSx,
					'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': outlineSx,
					'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': outlineSx,
					'& .MuiSelect-select': {
						display: 'flex',
						alignItems: 'center',
						minHeight: 'unset !important',
						paddingLeft: '16px !important',
						paddingRight: '40px !important',
						paddingTop: '12px !important',
						paddingBottom: '12px !important',
						color: selectTextColor,
						fontFamily: 'inherit',
						fontSize: 18,
						fontWeight: 400,
						lineHeight: '24px',
						letterSpacing: '-0.05px',
						transition: 'color 180ms ease, opacity 180ms ease'
					},
					'& .MuiSelect-icon': {
						color: iconColor,
						right: '16px',
						top: '50%',
						transform: 'translateY(-50%)',
						transition: 'color 180ms ease'
					},
					'& .MuiSelect-iconOpen': { transform: 'translateY(-50%)' }
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			{sortedOptions.map((option) => (
				<MenuItem
					key={option.value}
					value={option.value}
					onClick={() => {
						if (option.value === value && option.value !== 'all') {
							onChange?.('all');
							setIsMenuOpen(false);
						}
					}}
				>
					{option.label}
				</MenuItem>
			))}
		</TextField>
	);
};

export default SearchSectionSelect;
